<script lang="ts">
    import { handleSelectOption } from "./quiz-controller.svelte";

    export let stepIdx: number;
    export let style;
    export let options;
    export let onSelect: (option) => void;
    export let isShowing;
    export let optionType: "text_only" | "background_image" | "icon";

    const ICON_WIDTH = 56;
    const OPTION_REVEAL_DELAY = 400;
    const ROUNDED_CLASS_OPTION_MAP = [
        "rounded-tl-lg",
        "rounded-tr-lg",
        "rounded-bl-lg",
        "rounded-br-lg",
    ];
    const OPTION_HOVER_CLASSES = [
        "hover:scale-105",
        "hover:rounded-lg",
        "hover:font-semibold",
    ];
    if (optionType !== "background_image") {
        OPTION_HOVER_CLASSES.push("hover:shadow-xl");
    }

    let windowWidth: number;
    let windowHeight: number;
    let interactionEnabled = false;
    let container: HTMLElement;

    $: (async function reveal() {
        if (!isShowing) {
            return;
        }

        await new Promise((resolve) => {
            setTimeout(resolve, 350);
        });

        const optionsElems = container.querySelectorAll("button");
        optionsElems.forEach((elem, idx) => {
            setTimeout(() => {
                window.requestAnimationFrame(() => {
                    elem.classList.remove("opacity-0");
                    elem.classList.remove("translate-y-12");
                    if (style === "grid") {
                        elem.classList.add(ROUNDED_CLASS_OPTION_MAP[idx]);
                    }
                });
            }, idx * OPTION_REVEAL_DELAY);
        });

        setTimeout(() => {
            interactionEnabled = true;
        }, options.length * OPTION_REVEAL_DELAY);
    })();

    function onClickOption(option, event) {
        onSelect(option);

        const optionElem = event.currentTarget;
        optionElem.classList.add(
            "flash-animation",
            "pointer-events-none",
            ...OPTION_HOVER_CLASSES,
        );

        optionElem.addEventListener("animationend", () => {
            optionElem.classList.remove("flash-animation");
            optionElem.classList.add("brightness-[1.75]");
            setTimeout(() => {
                handleSelectOption(option);
            }, 350);
        });
    }
</script>

<svelte:window bind:innerWidth={windowWidth} bind:innerHeight={windowHeight} />

<div
    class={style === "grid"
        ? "grid size-full max-h-[95%] max-w-[448px] grid-cols-2 gap-3"
        : "flex size-full max-w-[448px] flex-col justify-center gap-3"}
    bind:this={container}
    data-step={stepIdx}
    class:pointer-events-none={!interactionEnabled}
>
    {#each options as option (option.title)}
        <button
            class={`
            option
            relative
            flex aspect-square translate-y-12 flex-col items-center
            justify-center gap-2
            overflow-hidden
            font-medium
            opacity-0 transition-all duration-200 sm:aspect-auto sm:text-lg
            ${optionType === "background_image" ? "bg-none p-0 text-[var(--primary-color)]" : "bg-[var(--primary-color)] p-2 text-[var(--text-on-primary-color)]"}
            ${style === "grid" ? "size-full" : "w-full rounded-lg"}
            ${OPTION_HOVER_CLASSES.join(" ")}
            `}
            on:click={(e) => onClickOption(option, e)}
        >
            {#if option.backgroundImageSrc}
                <img
                    src="{option.backgroundImageSrc}&width=400"
                    alt={option.title}
                    class="w-full flex-1 overflow-hidden rounded-lg object-cover sm:h-auto"
                />
            {/if}
            {#if option.iconSrc}
                <div
                    class="flex aspect-square max-h-[33%] flex-col items-center justify-center"
                >
                    <img
                        class="h-full w-full object-contain"
                        src={`${option.iconSrc}&width=${ICON_WIDTH}`}
                        alt={`${option.title} icon`}
                        loading="eager"
                    />
                </div>
            {/if}
            <div
                class={optionType === "background_image"
                    ? "relative max-h-[33%] w-full"
                    : style == "grid"
                      ? "size-full"
                      : ""}
            >
                <span
                    class="{style === 'grid' &&
                    optionType !== 'background_image'
                        ? 'absolute inset-0 p-2'
                        : ''}
                        flex flex-col items-center justify-center p-2 text-center text-sm sm:text-base
                    "
                >
                    {option.title}
                </span>
            </div>
        </button>
    {/each}
</div>

<style>
    /* Define the keyframes for the flash animation */
    @keyframes flash {
        0%,
        50% {
            filter: brightness(1.75);
        }
        100% {
            filter: brightness(1);
        }
    }

    /* The class to apply for the flash effect */
    .flash-animation {
        animation: flash 0.175s 1;
    }
</style>
